import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert, Box, Card, CardContent, Chip, Divider,
  Stack, ToggleButton, ToggleButtonGroup, Typography,
} from '@mui/material';
import DocumentService, {
  CategoryCount, ComplianceBoard as Board, BoardRow,
} from '../../api/services/document.service';
import { ObligationPill, ExpiryLine } from '../../components/files/vaultUI';
import FilesShell, { FileDocumentButton } from '../../components/files/FilesShell';
import PageHeader from '../../components/ui/PageHeader';
import { LoadingState } from '../../components/ui/States';
import ComplianceStrip from '../../components/files/ComplianceStrip';

// The AO's ranked worklist: every owner with something wrong, worst first.
// The server does the ranking — this screen must not re-sort, or the order
// stops matching what the phone shows the same person.

type Filter = 'all' | 'driver' | 'vehicle';

const ComplianceBoardPage: React.FC = () => {
  const navigate = useNavigate();
  const [board, setBoard] = useState<Board | null>(null);
  const [categories, setCategories] = useState<CategoryCount[]>([]);
  const [filter, setFilter] = useState<Filter>('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [boardData, categoryData] = await Promise.all([
        DocumentService.getBoard(),
        DocumentService.getCategories().catch(() => [] as CategoryCount[]),
      ]);
      setBoard(boardData);
      setCategories(categoryData);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load the compliance board');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const rows = (board?.rows ?? []).filter((row) => filter === 'all' || row.ownerType === filter);

  return (
    <FilesShell categories={categories} attentionCount={board?.summary.needsAttention ?? 0}
      onUploaded={load}
      header={
        <PageHeader
          title="Needs attention"
          subtitle="Everyone and everything with paperwork that needs work, worst first."
          actions={<FileDocumentButton />}
        />
      }
    >

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading ? (
        <LoadingState label="Loading the board…" />
      ) : (
        <>
          <ComplianceStrip board={board} />

          <ToggleButtonGroup
            size="small"
            exclusive
            value={filter}
            onChange={(_, value: Filter | null) => value && setFilter(value)}
            sx={{ mb: 2 }}
          >
            <ToggleButton value="all">Everything</ToggleButton>
            <ToggleButton value="driver">People</ToggleButton>
            <ToggleButton value="vehicle">Vehicles</ToggleButton>
          </ToggleButtonGroup>

          <Card>
            {rows.length === 0 ? (
              <CardContent>
                <Typography variant="body2" color="text.secondary">
                  Nothing needs attention{filter !== 'all' ? ' in this group' : ''}.
                  {board?.nextExpiry && filter === 'all'
                    && ` Next up is ${board.nextExpiry.label} for ${board.nextExpiry.ownerLabel}.`}
                </Typography>
              </CardContent>
            ) : (
              rows.map((row, index) => (
                <React.Fragment key={`${row.ownerType}-${row.ownerId ?? 'company'}`}>
                  {index > 0 && <Divider />}
                  <BoardRowView row={row} onOpen={() => navigate(`/files/entity/${row.ownerType}/${row.ownerId ?? 'company'}`)} />
                </React.Fragment>
              ))
            )}
          </Card>
        </>
      )}
    </FilesShell>
  );
};

const BoardRowView: React.FC<{ row: BoardRow; onOpen: () => void }> = ({ row, onOpen }) => {
  // Only the worst issue gets a line of its own; the rest are a count. A
  // row that lists six problems reads as six rows.
  const [worst, ...rest] = row.issues;
  return (
    <Box
      onClick={onOpen}
      sx={{ px: 2, py: 1.5, cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}
    >
      <Stack direction="row" alignItems="center" gap={2}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction="row" gap={1} alignItems="center">
            <Typography variant="body2" fontWeight={700} noWrap>{row.label}</Typography>
            {!row.tracked && <Chip size="small" variant="outlined" label="Not tracked" />}
          </Stack>
          {row.sublabel && (
            <Typography variant="caption" color="text.secondary" noWrap display="block">{row.sublabel}</Typography>
          )}
          {worst && (
            <Typography variant="caption" color="text.secondary">
              {worst.label}
              {worst.dueAt ? <> · <ExpiryLine expiresAt={worst.dueAt} /></> : null}
              {rest.length > 0 && ` · +${rest.length} more`}
            </Typography>
          )}
        </Box>
        {row.worstStatus && <ObligationPill status={row.worstStatus} />}
      </Stack>
    </Box>
  );
};

export default ComplianceBoardPage;
